<script setup lang="ts">
import { CheckCircleIcon, DotsCircleHorizontalIcon, ExclamationCircleIcon, XCircleIcon } from '@heroicons/vue/solid'
import IDNowLogo from '../../../../asset/IDNowLogo.vue'
import { computed, onMounted, onUnmounted, ref } from 'vue'
import LoadingSpinner from '../../../base/LoadingSpinner.vue'
import { getExternalKycProcess, postExternalKycProcess } from '../../../../utilities/api'
import { VerificationStatus } from '../../../../typings'
import { i18n } from '../../../../localization'
import { usePersonalAccountDataState } from '../../../../utilities/account/useAccountDataPersonal'
import AccountVerificationOverrideData from './AccountVerificationOverrideData.vue'

const verificationStatus = ref<VerificationStatus>('not_started')
const verificationLink = ref('')

const loading = ref(false)

const verificationStatusOptions = {
    not_started: {
        icon: DotsCircleHorizontalIcon,
        text: i18n.global.t('verification.status_not_started'),
        description: i18n.global.t('verification.status_not_started_description'),
        class: 'text-gray-400',
    },
    created: {
        icon: DotsCircleHorizontalIcon,
        text: i18n.global.t('verification.status_created'),
        description: i18n.global.t('verification.status_created_description'),
        class: 'text-blue-400',
    },
    pending: {
        icon: DotsCircleHorizontalIcon,
        text: i18n.global.t('verification.status_created'),
        description: i18n.global.t('verification.status_created_description'),
        class: 'text-blue-400',
    },
    in_review: {
        icon: DotsCircleHorizontalIcon,
        text: i18n.global.t('verification.status_in_review'),
        description: i18n.global.t('verification.status_in_review_description'),
        class: 'text-orange-400',
    },
    success: {
        icon: ExclamationCircleIcon,
        text: i18n.global.t('verification.status_data_changed'),
        description: i18n.global.t('verification.status_data_changed_description'),
        class: 'text-orange-400',
    },
    done: {
        text: i18n.global.t('verification.status_success'),
        description: i18n.global.t('verification.status_success_description'),
        icon: CheckCircleIcon,
        class: 'text-green-400',
    },
    failed_canceled: {
        icon: XCircleIcon,
        text: i18n.global.t('verification.status_failure'),
        description: i18n.global.t('verification.status_failure_description'),
        class: 'text-red-400',
    },
    failed_fraudulent: {
        icon: XCircleIcon,
        text: i18n.global.t('verification.status_failure'),
        description: i18n.global.t('verification.status_failure_description'),
        class: 'text-red-400',
    },
}

const currentVerificationStatus = computed(() => {
    return verificationStatusOptions[verificationStatus.value]
})

const buttonText = computed(() => {
    if (verificationStatus.value === 'created' || verificationStatus.value === 'pending') {
        return i18n.global.t('verification.id_now_kyc_continue_button')
    } else if (verificationStatus.value === 'in_review') {
        return i18n.global.t('verification.id_now_kyc_waiting_for_id_now_button')
    } else if (verificationStatus.value === 'failed_canceled' || verificationStatus.value === 'failed_fraudulent') {
        return i18n.global.t('verification.id_now_kyc_restart_button')
    } else {
        return i18n.global.t('verification.id_now_kyc_start_button')
    }
})

function getExternalKycProcessStatus() {
    if (verificationLink.value) {
        window.open(verificationLink.value, '_blank').focus()
    } else {
        postExternalKycProcess().then((response) => {
            verificationStatus.value = response.data.status
            verificationLink.value = response.data.redirect_url
            window.open(verificationLink.value, '_blank').focus()
            startKycProcessPolling()
        })
    }
}

async function fetchExternalKycProcess() {
    const response = await getExternalKycProcess()
    if (response.status === 200) {
        verificationStatus.value = response.data.status
        verificationLink.value = response.data.redirect_url
    }
}

let pollExternalKycStatus = null

function startKycProcessPolling() {
    if (
        verificationStatus.value === 'pending' ||
        verificationStatus.value === 'created' ||
        verificationStatus.value === 'in_review'
    ) {
        pollExternalKycStatus = setInterval(() => {
            fetchExternalKycProcess()
            if (
                verificationStatus.value !== 'pending' &&
                verificationStatus.value !== 'created' &&
                verificationStatus.value !== 'in_review'
            ) {
                usePersonalAccountDataState().fetchAccountData()
                clearInterval(pollExternalKycStatus)
            }
        }, 5000)
    }
}

onMounted(async () => {
    await fetchExternalKycProcess()
    startKycProcessPolling()
})

onUnmounted(() => {
    clearInterval(pollExternalKycStatus)
})
</script>

<template>
    <div>
        <div
            class="flex flex-wrap items-center space-y-3 md:space-y-0"
            :class="{ 'pt-4': verificationStatus !== 'done' && verificationStatus !== 'success' }">
            <div
                v-if="verificationStatus !== 'done' && verificationStatus !== 'success'"
                class="w-full items-center text-center md:w-1/2">
                <button
                    type="button"
                    :disabled="verificationStatus === 'in_review' || loading"
                    class="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-5 py-2 text-center text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:bg-slate-100"
                    @click="getExternalKycProcessStatus()">
                    <LoadingSpinner
                        v-if="verificationStatus === 'in_review' || loading"
                        class="inline-block text-gray-500"></LoadingSpinner>
                    <span class="w-full font-bold">{{ buttonText }}</span>
                </button>
                <div class="flex items-center justify-center">via <IDNowLogo class="my-4 ml-2 w-16"></IDNowLogo></div>
            </div>
            <div class="w-full pb-4 md:w-1/2">
                <div class="items-center md:flex">
                    <span class="pr-2 text-sm font-bold text-gray-600"
                        >{{ $t('account_settings.current_status') }}:
                    </span>
                    <div class="inline-flex items-center">
                        <Component
                            :is="currentVerificationStatus.icon"
                            :class="currentVerificationStatus.class"
                            class="inline-block w-5"></Component>
                        <div class="pl-2 text-sm text-gray-600">
                            <p>{{ currentVerificationStatus.text }}</p>
                        </div>
                    </div>
                </div>
                <p class="max-w-sm pt-1 text-xs text-gray-500">{{ currentVerificationStatus.description }}</p>
            </div>
            <div v-if="verificationStatus === 'success'" class="w-full items-center text-center">
                <AccountVerificationOverrideData></AccountVerificationOverrideData>
            </div>
        </div>
    </div>
</template>
